import { Component, Inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Track } from '@app/core/models/Track';
import { Bac } from '@app/library/models/bac.model';
import { BacsState } from '@app/library/states/bacs.state';
import {
  TrackMoveFailure,
  TrackMoveRequest,
  TrackMoveSuccess,
} from '@app/library/states/tracks.actions';
import { TracksState } from '@app/library/states/tracks.state';
import { getEllipsisTitle } from '@app/shared/utils';
import { Actions, ofActionDispatched, Store } from '@ngxs/store';
import { UiStoreHelperService } from '@radioking/shared/common-services';
import { SugestionInput } from '@radioking/ui/input';
import { Observable, Subscription } from 'rxjs';
import { filter, first, map, startWith } from 'rxjs/operators';

interface DataInput {
  isSingle: boolean;
  bac: Bac;
  track?: Track;
  trackIdList?: number[];
  currentBac?: Bac;
}

@Component({
  selector: 'rk-move-track-modal',
  templateUrl: './move-track-modal.component.html',
  styleUrls: ['./move-track-modal.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class MoveTrackModalComponent implements OnInit, OnDestroy {
  moveTrackForm: UntypedFormGroup;
  isLoading$: Observable<boolean>;
  hasError$: Observable<boolean>;
  isCreating$: Observable<boolean>;

  bacs$: Observable<SugestionInput[]>;

  subscription = new Subscription();

  constructor(
    public readonly dialogRef: MatDialogRef<MoveTrackModalComponent>,
    private readonly store: Store,
    private readonly actions$: Actions,
    private readonly uiStore: UiStoreHelperService,
    @Inject(MAT_DIALOG_DATA) public dataIn: DataInput,
  ) {}

  ngOnInit() {
    this.isLoading$ = this.uiStore.getLoadingState(TrackMoveRequest, [
      TrackMoveFailure,
      TrackMoveSuccess,
    ]);

    this.hasError$ = this.uiStore.getErrorState(TrackMoveRequest, TrackMoveFailure);

    let currentBacId = this.dataIn.currentBac
      ? this.dataIn.currentBac.id
      : this.dataIn.track
      ? this.dataIn.track.idTrackbox
      : 0;
    if (currentBacId === 0) {
      this.store
        .select(TracksState.trackWithId)
        .pipe(map(fn => fn(this.dataIn.trackIdList[0])))
        .pipe(first())
        .subscribe(data => (currentBacId = data.idTrackbox));
    }
    this.bacs$ = this.store
      .select(BacsState.unlockedBacsForSelect)
      .pipe(map(fn => fn(currentBacId)));

    const datas$ = this.bacs$.pipe(filter(data => !!data));
    this.isCreating$ = datas$.pipe(
      map(() => false),
      startWith(true),
    );
    this.subscription.add(datas$.subscribe(data => this.createForm(data)));
    this.subscription.add(
      this.actions$
        .pipe(ofActionDispatched(TrackMoveSuccess))
        .subscribe(() => this.dialogRef.close()),
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private createForm(bacs: SugestionInput[]) {
    this.moveTrackForm = new UntypedFormBuilder().group({
      targetBac: this.dataIn.bac
        ? bacs.find(bac => bac.id === this.dataIn.bac.id)
        : bacs[0],
    });
  }

  onSubmit() {
    const targetBac = this.store.selectSnapshot(BacsState.bacById)(
      this.moveTrackForm.value.targetBac.id,
    );
    if (this.dataIn.isSingle) {
      this.store.dispatch(new TrackMoveRequest([this.dataIn.track.id], targetBac));
    } else {
      this.store.dispatch(new TrackMoveRequest(this.dataIn.trackIdList, targetBac));
    }
  }

  onCancel() {
    this.dialogRef.close(false);
  }
  onAbort() {
    this.dialogRef.close(false);
  }

  getEllipsisTitle() {
    return getEllipsisTitle(this.dataIn.track);
  }
}
